import { useAddUserComment } from '@actions';
import Button from '@components/V4/Button';
import TextArea from '@components/V4/Inputs/Textarea';
import Modal from '@components/V4/Modal/Modal';
import DetailsModalSection from '@modules/Admin/components/DetailsModal/DetailsModalSection';
import { UserCommentDTO } from '@shared/clientTypes/DTOs/UserDTO';
import useToast from 'apps/agora/src/hooks/useToast';
import moment from 'moment';
import { useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { StudentDetailsPageContext } from '../StudentDetailsPage';

const VISIBLE_COMMENTS = 1;

const CommentCard = (props: { comment: UserCommentDTO }) => {
  const { comment } = props;

  return (
    <div className="rounded-lg bg-surfaceObject p-3">
      <p className="whitespace-pre-line break-words font-raleway text-xs text-white">
        {comment.text}
      </p>
      <p className="mt-2 font-raleway text-xxs text-customGrey">
        {comment.createdBy?.fullName ?? 'Unknown user'} ·{' '}
        {moment(comment.createdAt).format('DD.MM.YYYY HH:mm')}
      </p>
    </div>
  );
};

const StudentCommentsSection = () => {
  const { student } = useContext(StudentDetailsPageContext);

  const toast = useToast();
  const query = useQueryClient();

  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [text, setText] = useState('');

  const { mutate: addComment, isLoading } = useAddUserComment(student?._id ?? '', {
    onSuccess: async () => {
      setText('');
      setIsComposerOpen(false);
      await query.invalidateQueries('/users/:id/profile');
    },
    onError: () => {
      toast.error('Comment could not be saved.');
    },
  });

  const comments = student?.comments ?? [];
  const visibleComments = comments.slice(0, VISIBLE_COMMENTS);
  const hiddenCommentsCount = comments.length - visibleComments.length;

  const closeComposerHandler = () => {
    setIsComposerOpen(false);
    setText('');
  };

  const submitHandler = () => {
    const trimmedText = text.trim();

    if (!trimmedText) {
      toast.error('Please write a comment first.');
      return;
    }

    addComment({ text: trimmedText });
  };

  return (
    <DetailsModalSection
      className="mt-6"
      sectionTitle="Comments"
      buttonsArray={[{ buttonText: 'Add comment', onClick: () => setIsComposerOpen(true) }]}
    >
      <div className="flex flex-col gap-2">
        {visibleComments.length ? (
          visibleComments.map((comment) => <CommentCard key={comment._id} comment={comment} />)
        ) : (
          <p className="font-raleway text-xs text-customGrey">No comments yet.</p>
        )}

        {hiddenCommentsCount > 0 ? (
          <button
            type="button"
            onClick={() => setIsHistoryOpen(true)}
            className="self-start font-raleway text-xs text-customPrimary underline"
          >
            {hiddenCommentsCount} more {hiddenCommentsCount === 1 ? 'comment' : 'comments'}
          </button>
        ) : null}
      </div>

      <Modal isOpen={isComposerOpen} onClose={closeComposerHandler} size="small">
        <Modal.Header title="Add comment" />
        <Modal.Body className="gap-3">
          <TextArea
            name="comment"
            id="comment"
            label="Comment"
            placeholder="Write a comment about this student..."
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" buttonText="Cancel" onClick={closeComposerHandler} />
            <Button
              buttonText={isLoading ? 'Saving...' : 'Save comment'}
              onClick={submitHandler}
              disabled={isLoading || !text.trim()}
            />
          </div>
        </Modal.Body>
      </Modal>

      <Modal isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} size="small">
        <Modal.Header title={`Comments (${comments.length})`} />
        <Modal.Body className="max-h-[60vh] overflow-y-auto gap-3">
          <div className="flex flex-col gap-2">
            {comments.map((comment) => (
              <CommentCard key={comment._id} comment={comment} />
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </DetailsModalSection>
  );
};

export default StudentCommentsSection;
